import { useMemo, useState } from 'react';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { ArrayHelpers, ObjectHelpers } from '@headless-workspace/utils';
import { StoreInfoValue, WarehouseStockStatus } from '../../../../server';
import { useProductDeliveryInfo } from '../../delivery';
import { splitByStoreCategory } from './splitByStoreCategory';

export const useStoresDisplay = ({
    stores,
    productSku,
    favoriteStoreId,
    highlightedStoreId,
}: {
    stores: StoreInfoValue[];
    productSku: string;
    favoriteStoreId?: string;
    highlightedStoreId?: string;
}) => {
    const [isMoreStoresVisible, setIsMoreStoresVisible] = useState(false);
    const { result: productDeliveryInfoResult } = useProductDeliveryInfo(productSku);
    const productDeliveryInfo = unwrapResultSuccess(productDeliveryInfoResult);
    const isProductAvailableInWarehouse = productDeliveryInfo?.warehouse.stockStatus === WarehouseStockStatus.Available;

    const { availableStock, unavailableStock, highlightedStore, favoriteStore, unavailableLength } = useMemo(
        () =>
            splitByStoreCategory({
                stores,
                sku: productSku,
                favoriteStoreId,
                highlightedStoreId,
                isProductAvailableInWarehouse,
            }),
        [favoriteStoreId, highlightedStoreId, isProductAvailableInWarehouse, productSku, stores],
    );

    const shouldDisplayMoreStoresButton = useMemo(() => {
        return (
            !isMoreStoresVisible && ArrayHelpers.isNotEmpty(unavailableStock) && ArrayHelpers.isNotEmpty(availableStock)
        );
    }, [isMoreStoresVisible, unavailableStock, availableStock]);

    return {
        stores: [
            highlightedStore,
            favoriteStore,
            ...availableStock,
            ...(isMoreStoresVisible || !shouldDisplayMoreStoresButton ? unavailableStock : []),
        ].filter(ObjectHelpers.isNotNullishGuard<StoreInfoValue>),
        displayMoreStoresVisibleButton: shouldDisplayMoreStoresButton,
        setIsMoreStoresVisible,
        noStoresWithStockAvailable: !ArrayHelpers.isNotEmpty(availableStock) && unavailableLength !== 0,
    };
};
